import React, { useState, useMemo, useCallback } from 'react';
import { useToast, useOpenList, useLists } from '../../context';
import { useLang, useAutoTranslate, SOURCE_LANG } from '../../i18n';
import { useListNames } from '../../hooks/useListNames';
import { useStableGroups } from '../../hooks/useStable';
import { useLatest } from '../../hooks/useLatest';
import { useSaveFailed } from '../../hooks/useSaveFailed';
import { isHandled, isFlagged } from '../../logic/items';
import { normalizeName } from '../../logic/names';
import { localDateISO, fmtDate, fmtDateShort } from '../../logic/dates';
import { boardSheet } from '../../logic/preview';
import { MAX_ELSEWHERE } from '../../config/constants';
import { addItem, removeItem } from '../../data/items';
import { moveNames } from '../../data/lists';
import { makeItemActions } from '../../data/itemActions';
import { PreviewModal } from '../shared/PreviewModal';
import { ListEditor } from '../lists/ListEditor';
import { ListsManager } from '../lists/ListsManager';
import { TodayBanners } from './TodayBanners';
import { ListPanel } from './ListPanel';

const byList = item => item.category;
const NO_ITEMS = [];
const NO_NAMES = [];

/**
 * Today — the ONE open list, one panel per list, plus Preview / Shopping list sheets
 * and the list editor. Heavy lifting is memoized so a tick re-renders one row:
 * items are grouped per list once (stable arrays), the row actions are one object,
 * and the callbacks the panels get never change identity.
 *
 *   items     the open list (board items)      allItems  everything loaded (60 days)
 *   catalogs  { listId: suggestions }          author    who is noting
 */
export function TodayTab({ items, allItems, catalogs, author, onReview, onGoShopping }) {
  const { t, ln, lang } = useLang();
  const notify = useToast();
  const failed = useSaveFailed();
  const lists = useLists();
  const { name: listName, short } = useListNames();
  const { board, openMove, withList } = useOpenList();
  const today = localDateISO();
  const listDay = board ? board.listDay : today;

  // Telugu: auto-translate the names on screen (board, suggestions, renamed lists).
  // English needs nothing, so it skips building the list at all.
  useAutoTranslate(useMemo(() => {
    if (lang === SOURCE_LANG) return NO_NAMES;
    const set = new Set(items.map(i => i.name));
    Object.values(catalogs || {}).forEach(arr => (arr || []).forEach(s => set.add(s.name)));
    lists.active.forEach(l => { if (l.renamed) set.add(l.label); });
    return [...set];
  }, [lang, items, catalogs, lists.active]));

  const stats = useMemo(() => {
    const handled = items.filter(isHandled).length;
    const flagged = items.filter(isFlagged).length;
    return { total: items.length, handled, flagged, remaining: items.length - handled - flagged,
      pct: items.length ? Math.round((handled / items.length) * 100) : 0 };
  }, [items]);
  const { total, handled, flagged, remaining, pct } = stats;

  const [hideHandled, setHideHandled] = useState(true);   // default: only what's left; done items live in History
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [sheet, setSheet] = useState(null);                // 'preview' | 'shopping'
  const [editor, setEditor] = useState(null);              // { list } to edit, {} to create
  const [editingLists, setEditingLists] = useState(false); // ✏️ Edit lists view

  // ── What the panels get (stable identities → memoized panels and rows) ────
  const groups = useStableGroups(items, byList);
  // Names on the board per list (de-dupes adds, marks "✓ on the list"). Rebuilt only when a
  // name is added / removed / renamed / moved — not when an item is ticked.
  const takenKey = useMemo(() => items.map(i => `${i.category}|${normalizeName(i.name)}`).sort().join('\n'), [items]);
  const taken = useMemo(() => {
    const m = new Map();
    groups.forEach((rows, id) => m.set(id, new Set(rows.map(i => normalizeName(i.name)))));
    return m;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `groups` is covered by takenKey
  }, [takenKey]);
  // Event handlers read the latest values through a ref, so they never change identity.
  const latest = useLatest({ taken, lists, listName, short, author, notify, failed, t, ln, withList });

  const toggleCollapse = useCallback(id => setCollapsed(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  }), []);
  const openSettings = useCallback(list => setEditor({ list }), []);

  // Add `name` to list `listId` through the open list's gate. Landing somewhere other
  // than the panel it was typed in (`fromList`) says where, with undo.
  const addTo = useCallback((listId, name, fromList) => {
    const c = latest.current;
    const clean = String(name || '').trim();
    if (!clean || !c.lists.byId.has(listId)) return;
    if ((c.taken.get(listId) || new Set()).has(normalizeName(clean))) {
      c.notify(c.t('today.alreadyIn', { name: c.ln(clean), list: c.short(listId) }));
      return;
    }
    c.withList(listFor => {
      const added = addItem({ date: localDateISO(), listFor, category: listId, name: clean, qty: '', addedBy: c.author });
      added.saved.catch(c.failed);
      if (listId !== fromList) {
        c.notify(c.t('today.addedTo', { name: c.ln(clean), list: `${c.lists.catById(listId).icon} ${c.listName(listId)}` }),
          () => removeItem(added.id).catch(c.failed));
      }
    }, clean);
  }, [latest]);

  // Search across the other lists' suggestions (an item lives in one list — show it there):
  // exact matches first, then not-yet-listed, keeping each list's frecency order. Runs while
  // rendering a search, so it takes its data as real dependencies (not through the ref):
  // it changes only when names or lists change.
  const elsewhere = useCallback((fromId, nq) => {
    const own = new Set((catalogs[fromId] || []).map(s => normalizeName(s.name)));
    const out = [];
    lists.active.forEach(l => {
      if (l.id === fromId || l.noSuggest) return;
      (catalogs[l.id] || []).forEach(s => {
        const k = normalizeName(s.name);
        if (!k.includes(nq) || own.has(k)) return;
        out.push({ name: s.name, listId: l.id, on: (taken.get(l.id) || new Set()).has(k), exact: k === nq });
      });
    });
    return out.sort((a, b) => (b.exact - a.exact) || (a.on - b.on)).slice(0, MAX_ELSEWHERE);
  }, [catalogs, lists, taken]);

  const actions = useMemo(
    () => makeItemActions({ author, notify, t, lists, listName: short }),
    [author, notify, t, lists, short],
  );
  const moveTargets = useMemo(
    () => lists.active.map(l => ({ id: l.id, icon: l.icon, color: l.color, label: short(l) })),
    [lists.active, short],
  );

  // Items noted in a list their name no longer lives in (e.g. "Besan" noted under
  // Groceries before Chef Groceries existed) → one tap files them where they belong.
  const misfiled = useMemo(
    () => items.map(item => ({ item, to: lists.route(item.name, item.category) }))
      .filter(x => x.to !== x.item.category && lists.byId.has(x.to) && !lists.byId.get(x.to).deleted),
    [items, lists],
  );
  const tidy = () => {
    const moves = misfiled.map(x => ({ id: x.item.id, category: x.to, from: x.item.category }));
    moveNames({ items: moves.map(({ id, category }) => ({ id, category })) }).catch(failed);
    notify(t('tidy.done', { n: moves.length }),
      () => moveNames({ items: moves.map(m => ({ id: m.id, category: m.from })) }).catch(failed));
  };

  // Preview / print: the open list's still-needed items; "Shopping list" = the buy-lists
  // part of it, in the same sheet.
  const previewSections = useMemo(() => boardSheet(items, lists.active, listName, ln), [items, lists.active, listName, ln]);
  const shoppingSections = useMemo(
    () => previewSections.filter(s => lists.catById(s.key).sourceable), [previewSections, lists]);

  return (
    <div className="do-today">
      <TodayBanners total={total} hasHistory={allItems.length > 0} today={today}
        misfiled={misfiled} onTidy={tidy} onGoShopping={onGoShopping} />
      {total > 0 && (
        <div className="do-listhead">
          <span className="do-listhead__title">{t('list.forDay', { date: fmtDate(listDay) })}</span>
          {board && board.firstDay < listDay && (
            <span className="do-listhead__since">{t('list.since', { date: fmtDateShort(board.firstDay) })}</span>
          )}
          <span className="do-checklist__spacer" />
          {!(board && board.stale) && (   /* when stale, the banner above carries this button */
            <button className="do-listhead__move" onClick={openMove} title={t('list.moveHint')}>{t('list.moveBtn')}</button>
          )}
        </div>
      )}
      {total > 0 && (
        <div className="do-statusbar">
          <div className="do-statusbar__progress">
            <div className="do-statusbar__bar"><div className="do-statusbar__fill" style={{ width: `${pct}%` }} /></div>
            <span className="do-statusbar__label">
              <b>{handled}</b> {t('today.progressLabel', { total })}
              {remaining > 0 ? t('today.toGo', { n: remaining }) : (flagged === 0 ? t('today.allDone') : '')}
            </span>
          </div>
          <div className="do-statusbar__right">
            <button className="do-shopbtn" onClick={() => setSheet('preview')} title={t('today.previewHint')}>{t('common.preview')}</button>
            <button className="do-shopbtn" onClick={() => setSheet('shopping')} title={t('today.shoppingListHint')}>{t('today.shoppingList')}</button>
            {flagged > 0 && (
              <button className="do-statusbar__flag" onClick={onReview} title={t('today.toSourceHint')}>{t('today.toSource', { n: flagged })}</button>
            )}
            <button className={`do-focus${hideHandled ? ' do-focus--on' : ''}`} onClick={() => setHideHandled(v => !v)}
              disabled={handled === 0} title={handled === 0 ? t('today.nothingDoneYet') : t('today.hideDoneHint')}>
              {hideHandled ? t('today.showAll') : t('today.outstandingOnly')}
            </button>
          </div>
        </div>
      )}

      {/* ✏️ Edit lists — add a new list, reorder, rename, delete (like Closing's ✏️ Edit) */}
      <div className="do-boardbar">
        <span className="do-boardbar__lbl">{t('lists.boardLabel', { n: lists.active.length })}</span>
        <span className="do-checklist__spacer" />
        <button className={`do-focus${editingLists ? ' do-focus--on' : ''}`} onClick={() => setEditingLists(v => !v)}
          aria-pressed={editingLists}>
          {editingLists ? t('lists.doneEditing') : t('lists.editMode')}
        </button>
      </div>

      {editingLists ? (
        <ListsManager boardItems={items} allItems={allItems} author={author}
          onAdd={() => setEditor({})} onEdit={openSettings} />
      ) : (
        <div className="do-panels">
          {lists.active.map(list => (
            <ListPanel key={list.id} list={list} items={groups.get(list.id) || NO_ITEMS} catalog={catalogs[list.id] || NO_ITEMS}
              author={author} hideHandled={hideHandled} collapsed={collapsed.has(list.id)}
              onToggleCollapse={toggleCollapse} onSettings={openSettings} addTo={addTo} elsewhere={elsewhere}
              actions={actions} moveTargets={moveTargets} />
          ))}
          <button className="do-newlist" onClick={() => setEditor({})}>
            <span className="do-newlist__plus" aria-hidden="true">＋</span>
            <span className="do-newlist__txt">
              <b className="do-newlist__title">{t('lists.new')}</b>
              <span className="do-newlist__sub">{t('lists.newHint')}</span>
            </span>
          </button>
        </div>
      )}
      <p className="do-today__foot">📅 {fmtDate(today)} · {t('today.savesSync')}</p>

      {editor && (
        <ListEditor list={editor.list && (lists.byId.get(editor.list.id) || editor.list)} catalogs={catalogs}
          boardItems={items} allItems={allItems} author={author} onClose={() => setEditor(null)} />
      )}
      {sheet === 'shopping' && (
        <PreviewModal title={t('preview.shoppingTitle')}
          subtitle={n => t('preview.shoppingSubtitle', { date: fmtDate(listDay), n })}
          sections={shoppingSections} onClose={() => setSheet(null)} notify={notify} />
      )}
      {sheet === 'preview' && (
        <PreviewModal title={t('preview.dailyTitle')} subtitle={n => t('preview.dailySubtitle', { date: fmtDate(listDay), n })}
          sections={previewSections} onClose={() => setSheet(null)} notify={notify} />
      )}
    </div>
  );
}
